import { ExternalLink, Mail, Handshake, ArrowRight } from "lucide-react";
import { useT, useLang } from "@/lib/i18n";
import MarketingNav from "@/components/MarketingNav";
import MarketingFooter from "@/components/MarketingFooter";

const CONTACT_EMAIL = "owners@myratchet.com";

// monday.com brand dots
function MondayLogo() {
  return (
    <div className="flex items-center gap-2" aria-label="monday.com">
      <div className="flex items-center gap-1">
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#ff3d57" }} />
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#ffcb00" }} />
        <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: "#00c875" }} />
      </div>
      <span className="font-display font-black text-2xl tracking-tight lowercase">monday.com</span>
    </div>
  );
}

const PARTNERS = [
  {
    key: "monday",
    logo: <MondayLogo />,
    url: "https://monday.com",
    category: { pt: "Work OS · Gestão de trabalho", en: "Work OS · Work management", es: "Work OS · Gestión de trabajo" },
    status: { pt: "Parceria ativa", en: "Active partnership", es: "Alianza activa" },
    blurb: {
      pt: "Levamos a investigação automatizada de bugs do Ratchet direto aos boards do monday.com. Um item de suporte vira diagnóstico de causa-raiz — parse, query, correlação e resolução — sem o time sair do Work OS.",
      en: "We bring Ratchet's automated bug investigation straight into monday.com boards. A support item becomes a root-cause diagnosis — parse, query, correlate and resolve — without your team leaving the Work OS.",
      es: "Llevamos la investigación automatizada de bugs de Ratchet directo a los tableros de monday.com. Un ticket se convierte en diagnóstico de causa raíz — parse, query, correlación y resolución — sin salir del Work OS.",
    },
  },
];

export default function Partners() {
  const t = useT();
  const { lang } = useLang();

  const heroTitle = { pt: "Parceiros", en: "Partners", es: "Socios" }[lang];
  const heroLead = {
    pt: "O Ratchet vive onde seu time já trabalha. Fazemos parceria com as plataformas que sustentam operações de engenharia e suporte — para que a investigação automatizada aconteça dentro do fluxo, não fora dele.",
    en: "Ratchet lives where your team already works. We partner with the platforms that power engineering and support operations — so automated investigation happens inside the flow, not beside it.",
    es: "Ratchet vive donde tu equipo ya trabaja. Nos aliamos con las plataformas que sostienen operaciones de ingeniería y soporte — para que la investigación automatizada ocurra dentro del flujo, no al lado.",
  }[lang];

  const overline = { pt: "parceiros", en: "partners", es: "socios" }[lang];
  const featuredOverline = { pt: "parceiro em destaque", en: "featured partner", es: "socio destacado" }[lang];
  const ctaTitle = { pt: "Quer construir com a gente?", en: "Want to build with us?", es: "¿Quieres construir con nosotros?" }[lang];
  const ctaLead = {
    pt: "Plataformas, integradores e revendas: se seus clientes vivem apagando incêndio em produção, temos um caminho de parceria pra você.",
    en: "Platforms, integrators and resellers: if your customers live firefighting production, we have a partnership path for you.",
    es: "Plataformas, integradores y revendedores: si tus clientes viven apagando incendios en producción, tenemos un camino de alianza para ti.",
  }[lang];
  const ctaBtn = { pt: "Falar sobre parceria", en: "Talk partnerships", es: "Hablar de alianzas" }[lang];
  const visit = { pt: "Visitar site", en: "Visit site", es: "Visitar sitio" }[lang];

  return (
    <div className="min-h-screen" data-testid="partners-page">
      <MarketingNav />

      <section className="max-w-5xl mx-auto px-6 lg:px-8 pt-24 pb-12">
        <div className="overline mb-4">// {overline}</div>
        <h1 className="font-display font-black text-5xl lg:text-6xl tracking-tighter mb-6" data-testid="partners-title">
          {heroTitle}
        </h1>
        <p className="text-lg text-white/70 max-w-3xl leading-relaxed">{heroLead}</p>
      </section>

      {/* Featured partners */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-16">
        <div className="overline mb-6">// {featuredOverline}</div>
        <div className="grid grid-cols-1 gap-6">
          {PARTNERS.map((p) => (
            <div
              key={p.key}
              className="surface p-6 lg:p-8 hover:border-primary/40 transition-colors"
              data-testid={`partner-${p.key}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                {p.logo}
                <span className="inline-flex items-center gap-2 text-xs font-mono" style={{ color: "#00c875" }}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: "#00c875" }} />
                  {p.status[lang] ?? p.status.en}
                </span>
              </div>
              <div className="overline mt-5 mb-3">{p.category[lang] ?? p.category.en}</div>
              <p className="text-white/70 leading-relaxed max-w-3xl">{p.blurb[lang] ?? p.blurb.en}</p>
              <a
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-1.5 text-sm text-white hover:text-primary transition-colors"
              >
                <span>{visit}</span>
                <ExternalLink className="w-3.5 h-3.5 opacity-70" />
              </a>
            </div>
          ))}
        </div>
      </section>

      {/* Become a partner CTA */}
      <section className="max-w-5xl mx-auto px-6 lg:px-8 pb-24">
        <div className="surface p-8 lg:p-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-2 mb-3 text-white/50">
              <Handshake className="w-4 h-4" />
              <span className="overline">{lang === "pt" ? "seja parceiro" : lang === "es" ? "sé socio" : "become a partner"}</span>
            </div>
            <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight mb-2">{ctaTitle}</h2>
            <p className="text-white/60 leading-relaxed">{ctaLead}</p>
          </div>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("Ratchet — Partnership")}`}
            className="btn-primary text-sm py-3 px-5 inline-flex items-center gap-2 shrink-0 whitespace-nowrap"
            data-testid="partners-cta"
          >
            <Mail className="w-4 h-4" />
            <span>{ctaBtn}</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
